/**
 * CSV export — RFC 4180 serialisation with formula-injection neutralisation.
 *
 * This is the ONE implementation of `toCsv` / `downloadCsv`. The higher-tier React utilities package
 * re-exports it, so every existing call site keeps working unchanged.
 *
 * Escaping (RFC 4180): a cell containing the delimiter, a double quote, CR or LF is wrapped in double
 * quotes and inner quotes are doubled. `null` / `undefined` and non-finite numbers become empty cells.
 *
 * Text cells that a spreadsheet could evaluate as a formula (`=`, `+`, `-`, `@`, TAB / CR prefixes) are
 * prefixed with `'` by default — see ./formula.ts. Numbers are never altered.
 *
 * @example
 * ```ts
 * const csv = toCsv(["Name", "Score"], [["Alice", 95], ["Bob", null]]);
 * downloadCsv("results", ["Name", "Score"], [["Alice", 95], ["Bob", null]]);
 * ```
 */

import { neutraliseFormula } from "./formula";

export type CsvCell = string | number | boolean | Date | null | undefined;

export interface ToCsvOptions {
  /** Cell separator (default `,`). */
  delimiter?: string;
  /** Line terminator (default CRLF per RFC 4180; no trailing terminator is added). */
  eol?: string;
  /**
   * Prefix risky text cells with `'` (default `true`). Only switch this off for a machine-to-machine
   * file that no person will open in a spreadsheet.
   */
  neutraliseFormulas?: boolean;
  /** Prepend a UTF-8 byte-order mark so Excel detects UTF-8 (default `false`). */
  bom?: boolean;
}

function cellToString(value: CsvCell): { text: string; isText: boolean } {
  if (value == null) return { text: "", isText: false };
  if (typeof value === "number") return { text: Number.isFinite(value) ? String(value) : "", isText: false };
  if (typeof value === "boolean") return { text: value ? "true" : "false", isText: false };
  if (value instanceof Date) return { text: Number.isNaN(value.getTime()) ? "" : value.toISOString(), isText: false };
  return { text: String(value), isText: true };
}

function escapeCell(value: CsvCell, delimiter: string, neutralise: boolean): string {
  const { text, isText } = cellToString(value);
  const safe = neutralise && isText ? neutraliseFormula(text) : text;
  if (safe.includes(delimiter) || safe.includes('"') || safe.includes("\n") || safe.includes("\r")) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

/**
 * Build a CSV string from headers and rows.
 *
 * @param headers - Column header labels (neutralised like any other text cell)
 * @param rows    - Data rows
 * @returns CSV text, CRLF line endings by default, no trailing newline
 */
export function toCsv(
  headers: readonly string[],
  rows: ReadonlyArray<ReadonlyArray<CsvCell>>,
  options: ToCsvOptions = {}
): string {
  const { delimiter = ",", eol = "\r\n", neutraliseFormulas = true, bom = false } = options;
  const lines: string[] = [headers.map((h) => escapeCell(h, delimiter, neutraliseFormulas)).join(delimiter)];
  for (const row of rows) {
    lines.push(row.map((cell) => escapeCell(cell, delimiter, neutraliseFormulas)).join(delimiter));
  }
  return (bom ? "\uFEFF" : "") + lines.join(eol);
}

/**
 * Build a CSV string and trigger a browser file download.
 *
 * @param filename - Desired filename; `.csv` is appended when the extension is absent.
 *
 * SSR-safe: a no-op when `document` is not available (and on React Native).
 */
export function downloadCsv(
  filename: string,
  headers: readonly string[],
  rows: ReadonlyArray<ReadonlyArray<CsvCell>>,
  options?: ToCsvOptions
): void {
  if (typeof document === "undefined") return;

  const normalizedFilename = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  const content = toCsv(headers, rows, options);
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", normalizedFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
